// Fork: public incident history, three months per page.
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { StatusHeader } from '../../components/status/StatusHeader'
import {
  LEVEL_BAR_CLASS,
  formatDateTime,
  formatTimeOfDay,
  isResolved,
  useStatusBranding,
  useStatusHref,
  type StatusIncidentDetail,
} from '../../components/status/statusShared'

interface StatusHistoryData {
  timezone: string
  start: string
  end: string
  previous: string
  next?: string
  components: { key: string; name: string }[]
  incidents: StatusIncidentDetail[]
}

function monthDate(month: string): Date {
  const [year = 1970, monthIndex = 1] = month.split('-').map(Number)
  return new Date(year, monthIndex - 1, 1)
}

function formatMonth(month: string, withYear: boolean): string {
  return monthDate(month).toLocaleDateString(undefined, withYear ? { month: 'short', year: 'numeric' } : { month: 'long' })
}

/** Months from end back to start (YYYY-MM), newest first. */
function monthsInRange(start: string, end: string): string[] {
  const months: string[] = []
  for (let cursor = monthDate(end); cursor >= monthDate(start); cursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1)) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`)
  }
  return months
}

function localMonth(iso: string): string {
  const date = new Date(iso)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function HistoryIncident({ incident }: { incident: StatusIncidentDetail }) {
  const href = useStatusHref()
  const [expanded, setExpanded] = useState(false)
  const latest = incident.updates[0]
  return (
    <li className="flex gap-3">
      <span className={`w-1 flex-shrink-0 rounded-full ${LEVEL_BAR_CLASS[incident.level]}`} aria-hidden="true" />
      <div className="flex-1 min-w-0 py-0.5">
        <div className="flex items-start justify-between gap-3">
          <Link to={href(`/incidents/${incident.id}`)} className="text-sm sm:text-base font-medium text-gray-900 hover:underline break-words">
            {incident.title}
          </Link>
          <time dateTime={incident.triggered_at} className="text-xs text-gray-500 flex-shrink-0 mt-1">{formatTimeOfDay(incident.triggered_at)}</time>
        </div>
        {latest && !expanded && <p className="mt-1 text-sm text-gray-600 line-clamp-2 break-words">{latest.message}</p>}
        {!isResolved(incident) && <p className="mt-1 text-xs font-medium text-red-700">Ongoing</p>}
        {incident.updates.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
            {expanded ? 'Hide updates' : `Show ${incident.updates.length} update${incident.updates.length === 1 ? '' : 's'}`}
          </button>
        )}
        {expanded && (
          <ol className="mt-2 space-y-3 border-l border-gray-200 pl-3">
            {incident.updates.map((update, index) => (
              <li key={`${update.created_at}-${index}`}>
                <p className="text-sm font-medium text-gray-900">{update.label || 'Update'}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{update.message}</p>
                <p className="text-xs text-gray-500">{formatDateTime(update.created_at)}</p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </li>
  )
}

function ComponentFilter({ components, selected, onChange }: {
  components: { key: string; name: string }[]
  selected: string[]
  onChange: (keys: string[]) => void
}) {
  if (components.length === 0) return null
  return (
    <details className="relative">
      <summary className="list-none cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
        {selected.length === 0 ? 'Filter components' : `${selected.length} component${selected.length === 1 ? '' : 's'}`}
        <ChevronDown className="w-4 h-4" aria-hidden="true" />
      </summary>
      <fieldset className="absolute right-0 z-20 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3 space-y-2">
        <legend className="sr-only">Components</legend>
        {components.map(component => (
          <label key={component.key} className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={selected.includes(component.key)}
              onChange={event => onChange(event.target.checked ? [...selected, component.key] : selected.filter(key => key !== component.key))}
              className="rounded border-gray-300"
            />
            {component.name}
          </label>
        ))}
        {selected.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="text-xs text-gray-500 hover:text-gray-900 underline">Clear filter</button>
        )}
      </fieldset>
    </details>
  )
}

export function StatusHistoryPage() {
  const href = useStatusHref()
  const summary = useStatusBranding()
  const [params, setParams] = useSearchParams()
  const end = params.get('end') ?? ''
  const [data, setData] = useState<StatusHistoryData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    let cancelled = false
    setError(null)
    window.fetch(`/api/v1/status/history${end ? `?end=${encodeURIComponent(end)}` : ''}`)
      .then(res => {
        if (!res.ok) throw new Error('Unable to load incident history.')
        return res.json() as Promise<StatusHistoryData>
      })
      .then(json => { if (!cancelled) setData(json) })
      .catch((failure: Error) => { if (!cancelled) setError(failure.message) })
    return () => { cancelled = true }
  }, [end])

  const incidents = (data?.incidents ?? []).filter(incident =>
    selected.length === 0 || incident.components.some(component => selected.includes(component.key)))
  const title = summary?.branding.title || `${summary?.org_name || 'System'} Status`

  return (
    <div className="min-h-screen bg-gray-50">
      <StatusHeader branding={summary?.branding} orgName={summary?.org_name} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <Link to={href('/')} className="hover:text-gray-900 hover:underline">{title}</Link>
          <span aria-hidden="true"> / </span>
          <span className="text-gray-900">History</span>
        </nav>
        <div className="mt-4 pb-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">History</h2>
            {data && (
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <button type="button" onClick={() => setParams({ end: data.previous })} aria-label="Earlier months" className="p-1 hover:text-gray-900">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span>{formatMonth(data.start, true)} – {formatMonth(data.end, true)}</span>
                <button
                  type="button"
                  disabled={!data.next}
                  onClick={() => data.next && setParams({ end: data.next })}
                  aria-label="Later months"
                  className="p-1 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-500"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          {data && <ComponentFilter components={data.components} selected={selected} onChange={setSelected} />}
        </div>

        {error && <p className="mt-6 text-sm text-gray-600">{error}</p>}
        {!data && !error && <div className="mt-6 h-40 bg-white rounded-lg border border-gray-200 animate-pulse" />}
        {data && monthsInRange(data.start, data.end).map(month => {
          const monthIncidents = incidents.filter(incident => localMonth(incident.triggered_at) === month)
          const days = [...new Set(monthIncidents.map(incident => new Date(incident.triggered_at).getDate()))]
          return (
            <section key={month} className="mt-8">
              <h3 className="text-base font-semibold text-gray-900">{formatMonth(month, false)}</h3>
              {monthIncidents.length === 0 ? (
                <p className="mt-3 text-sm text-gray-500">No incidents reported.</p>
              ) : (
                days.map(day => {
                  const dayIncidents = monthIncidents.filter(incident => new Date(incident.triggered_at).getDate() === day)
                  const first = dayIncidents[0]
                  if (!first) return null
                  const weekday = new Date(first.triggered_at).toLocaleDateString(undefined, { weekday: 'short' })
                  return (
                    <div key={day} className="mt-5 pb-5 border-b border-gray-100 last:border-0 flex gap-4">
                      <p className="w-14 flex-shrink-0 text-sm"><span className="font-semibold text-gray-900">{day}</span> <span className="text-gray-500">{weekday}</span></p>
                      <ul className="flex-1 min-w-0 space-y-5">
                        {dayIncidents.map(incident => <HistoryIncident key={incident.id} incident={incident} />)}
                      </ul>
                    </div>
                  )
                })
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
}
